import Delete from "@mui/icons-material/Delete";
import Search from "@mui/icons-material/Search";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { DataGridWithDefaultSort } from "components/DataGridWithDefaultSort";
import { JobStepsDone } from "generated-graphql/graphql";
import { useMemo, useState } from "react";
import { prettyPrintDateTimeWithSeconds } from "util/dates";
import { useReportDetails } from "../useReportDetails";

export interface StepsDoneTabProps {
  stepsDone: JobStepsDone[];
  onDeleteStep: (step: JobStepsDone) => void;
  showChemicalProgress?: boolean;
}

const CHEMICAL_ENTRY_PREFIXES = [
  "chemical-",
  "product-",
  "addProduct-",
  "addChemical-",
  "enterChemical-",
  "rfc-",
  "Add Chemical-",
  "Set Additional Chemical Info-",
] as const;

function isChemicalEntryStep(name: string | null | undefined): boolean {
  if (!name) {
    return false;
  }
  return CHEMICAL_ENTRY_PREFIXES.some((prefix) => name.startsWith(prefix));
}

export function StepsDoneTab({
  stepsDone,
  onDeleteStep,
  showChemicalProgress = false,
}: StepsDoneTabProps) {
  const { data } = useReportDetails();
  const totalChemicals = data?.tierIIReport.productCount ?? null;
  const [search, setSearch] = useState("");

  const filteredSteps = useMemo(() => {
    if (!search.trim()) {
      return stepsDone;
    }
    const lower = search.toLowerCase();
    return stepsDone.filter((step) =>
      (step.name ?? "").toLowerCase().includes(lower)
    );
  }, [stepsDone, search]);

  const enteredChemicalsCount = useMemo(() => {
    const uniqueStepNames = new Set<string>();
    for (const step of stepsDone) {
      if (step.name && isChemicalEntryStep(step.name)) {
        uniqueStepNames.add(step.name);
      }
    }
    return uniqueStepNames.size;
  }, [stepsDone]);

  const columns: GridColDef<JobStepsDone>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.75,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        width: 180,
        renderCell: ({ row }) => (
          <span>{prettyPrintDateTimeWithSeconds(row.updatedAt)}</span>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        getActions: (params: GridRowParams<JobStepsDone>) => [
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => onDeleteStep(params.row)}
          />,
        ],
      },
    ],
    [onDeleteStep]
  );

  const chemicalProgressLabel = showChemicalProgress
    ? `Chemicals entered: ${enteredChemicalsCount}${
        totalChemicals != null ? `/${totalChemicals}` : ""
      }`
    : null;

  const CustomFooter = useMemo(
    () =>
      function Footer() {
        return (
          <GridFooterContainer>
            {chemicalProgressLabel ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                {chemicalProgressLabel}
              </Typography>
            ) : (
              <Box />
            )}
            <GridPagination />
          </GridFooterContainer>
        );
      },
    [chemicalProgressLabel]
  );

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search step name..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      <DataGridWithDefaultSort
        autoHeight
        pagination
        pageSizeOptions={[5, 10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sortModel={[{ field: "createdAt", sort: "desc" }]}
        columns={columns}
        rows={filteredSteps}
        slots={{ footer: CustomFooter }}
      />
    </Box>
  );
}
